import { z } from 'zod';
import { reportTypeSchema } from '../../types/datacrateModel.schema';
import { deliveryDecisionSchema, documentQualityDecisionV1Schema } from '../../types/reportContractsV2';
import { demonstrationDisclosureV1Schema } from '../../types/demonstration';
import { hashCanonicalJson } from '../reportGeneration/reportContractHash';

export const REPORT_SUMMARY_BUILDER_VERSION = '1.0.0' as const;
export const REPORT_SUMMARY_SCHEMA_VERSION = 'report-summary-v1' as const;
const text = z.string().min(1);
const hash = z.string().regex(/^[a-f0-9]{64}$/);
const fragment = z.object({
  id: text, sectionId: text, sectionTitle: text, sourceBlockId: text,
  sourceItemId: text.optional(), sourceContentHash: hash, text,
  referenceIndices: z.array(z.number().int().positive()),
}).strict();
const page = <T extends 'overview' | 'findings' | 'actions-sources'>(id: T) => z.object({
  id: z.literal(id), title: text, entries: z.array(fragment),
}).strict();
export const reportSummaryModelV1Schema = z.object({
  schemaVersion: z.literal(REPORT_SUMMARY_SCHEMA_VERSION),
  builderVersion: z.literal(REPORT_SUMMARY_BUILDER_VERSION),
  modelHash: hash,
  identity: z.object({
    reportId: text, revisionId: text, sourceDocumentHash: hash, reportType: reportTypeSchema,
    siteId: text.optional(), siteName: text, address: z.string(), sourceGeneratedAt: z.string(),
    sourceVersion: z.object({ major: z.number().int().positive(), minor: z.number().int().nonnegative() }).strict(),
  }).strict(),
  title: text, reportTypeLabel: text, summary: z.string(),
  recommendation: z.string().optional(),
  qualityDecision: documentQualityDecisionV1Schema,
  deliveryDecision: deliveryDecisionSchema,
  demonstrationDisclosure: demonstrationDisclosureV1Schema.optional(),
  pages: z.tuple([page('overview'), page('findings'), page('actions-sources')]),
  sources: z.array(z.object({
    index: z.number().int().positive(), label: text, url: z.string().optional(),
    retrievedAt: z.string(), field: z.string(), documentId: z.string().optional(), pageNumber: z.number().int().positive().optional(),
    qualification: z.string().optional(), sourceGrade: z.string().optional(), dataAsOf: z.string().optional(),
    scopeDetails: z.array(z.object({ topic: z.string(), detail: z.string() }).strict()),
  }).strict()),
  gaps: z.array(z.object({ sectionId: text, title: text, reason: text, detail: text }).strict()),
  omittedSelections: z.array(z.object({ sectionId: text, title: text, count: z.number().int().nonnegative(), reason: text }).strict()),
  legalNotice: z.array(text),
}).strict();
export type ReportSummaryModelV1 = z.infer<typeof reportSummaryModelV1Schema>;
export type ReportSummaryEntry = ReportSummaryModelV1['pages'][number]['entries'][number];

/** Exact deterministic projection identity, separate from the full document. */
export function hashReportSummaryModel(model: Omit<ReportSummaryModelV1, 'modelHash'> | ReportSummaryModelV1): string {
  const { modelHash: _modelHash, ...content } = model as ReportSummaryModelV1;
  return hashCanonicalJson(content);
}
